import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import AdvancedFilters from '../AdvancedFilters/AdvancedFilters';

/**
 * FilterDialog - Modal para filtros avançados
 * 
 * Exibe os filtros em um dialog/modal para melhor UX,
 * evitando que os filtros cubram os cards da página.
 */
const FilterDialog = ({
  open,
  onClose,
  onApply,
  onClearFilters,
  // Todas as props dos filtros
  ...filterProps
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
    onClose();
  };

  const handleClearAndClose = () => {
    if (onClearFilters) {
      onClearFilters();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          maxWidth: 900,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ py: 2, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon color="primary" />
            <Typography variant="h6">
              Filtros Avançados
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        <AdvancedFilters
          {...filterProps}
          onClearFilters={onClearFilters}
          hideHeader={true}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleClearAndClose}
          startIcon={<ClearIcon />}
          color="error"
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Limpar Filtros
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="text"
          sx={{
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          startIcon={<FilterIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Aplicar Filtros
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FilterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func,
  onClearFilters: PropTypes.func,
  // Todas as props dos filtros serão passadas via spread operator
};

export default FilterDialog;

